"use client";

import React, { useEffect, useRef, useState } from "react";
import SectionHeader from "./section-header";
import Image from "next/image";

import { howItWorksSteps } from "@/lib/data";

const HowItWorks = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.3 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      className="2xl:container 2xl:mx-auto 2xl:px-4 sm:px-4"
      id="how-it-works"
    >
      <div
        ref={sectionRef}
        // NOte: made the padding here top and bottom as opposed to when there was a button below
        // pb-7.25 md:pb-5.25 lg:pb-7.25  xl:pb-10.25 
        className="space-y-10 sm:space-y-8 lg:space-y-12 xl:space-y-16 px-4 md:px-2 lg:px-5 xl:px-8 py-8 md:py-6 lg:py-9 xl:py-12  bg-[linear-gradient(94.21deg,#F6F7F7_-0.97%,rgba(230,240,255,0.5)_95.54%)]"
      >
        <div className="grid place-self-center space-y-4 sm:space-y-5 lg:space-y-7 xl:space-y-12">
          <SectionHeader title="How It Works" />
          <div className="space-y-2.5 lg:space-y-4 xl:space-y-6 text-center">
            <div className="overflow-hidden">
              <h2
                className={`font-semibold leading-7.5 sm:leading-9 lg:leading-11  xl:leading-14 text-[21px] sm:text-[28px] lg:text-[37px] xl:text-5xl transition-transform duration-[1500ms] delay-[300ms] ease-[cubic-bezier(0.175,0.885,0.32,1.275)] ${
                  isVisible ? "translate-y-0" : "translate-y-[120%]"
                }`}
              >
                How BrimmHQ Supports You
              </h2>
            </div>
            <p
              className={`leading-[1.7] sm:leading-normal text-sm xl:text-base font-normal transition-all duration-1000 delay-[500ms] ease-out ${
                isVisible
                  ? "opacity-100 translate-y-0"
                  : "opacity-0 translate-y-4"
              }`}
            >
              From sign-up to ongoing support, every step is designed around
              your maternal journey.
            </p>
          </div>
        </div>

        <div className="relative w-full md:text-center">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 sm:gap-8 md:gap-y-0 lg:gap-x-8 xl:gap-x-12">
            {howItWorksSteps.map((step, index) => {
              const baseDelay = 600;
              const stepDelay = baseDelay + index * 500;

              return (
                <div
                  key={step.title}
                  className=" gap-x-5 md:space-y-5 lg:space-y-8 xl:space-y-12  flex  md:block relative z-10"
                >
                  <div
                    className={`relative z-30 shrink-0 size-10 lg:size-12 xl:size-16 md:grid md:place-self-center transform transition-all duration-[600ms] ease-[cubic-bezier(0.34,1.56,0.64,1)] ${
                      isVisible ? "scale-100 opacity-100" : "scale-50 opacity-0"
                    }`}
                    style={{ transitionDelay: `${stepDelay}ms` }}
                  >
                    <Image
                      alt={`step ${step.id} icon`}
                      src={`/images/how-it-works/${step.icon}`}
                      fill
                      className="object-cover object-center"
                    />
                  </div>

                  <div
                    className={`pt-1.5 space-y-3 sm:space-y-2 lg:space-y-2.5 xl:space-y-4 transition-all duration-700 ease-out ${
                      isVisible
                        ? "opacity-100 translate-y-0"
                        : "opacity-0 translate-y-6"
                    }`}
                    style={{ transitionDelay: `${stepDelay + 200}ms` }}
                  >
                    <div className="w-fit md:grid md:place-self-center rounded-[20px] bg-surface-white outline-[0.5px] outline-surface-light/70 py-1 lg:py-1.5 xl:py-2 px-4.5 xl:px-6 font-medium text-sm sm:text-xs lg:text-sm xl:text-base text-primary">
                      {step.step}
                    </div>

                    <div className="text-surface space-y-1.5 sm:space-y-2 lg:space-y-2.5 xl:space-y-4">
                      <h3 className="font-semibold text-base sm:text-[17px] lg:text-xl xl:text-2xl">
                        {step.title}
                      </h3>
                      <p className="text-sm sm:text-xs lg:text-sm xl:text-base leading-6 sm:leading-4.5 lg:leading-5.5">
                        {step.description}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          <div
            className={`absolute w-0.5 md:w-4/5 bg-primary h-[80%] md:h-0.5 md:top-5.5 xl:top-7.5 left-4.5 top-5 md:left-1/2 md:-translate-x-1/2 origin-top md:origin-left transform transition-transform duration-[2500ms] delay-[600ms] ease-in-out ${
              isVisible
                ? "scale-y-100 md:scale-x-100"
                : "scale-y-0 md:scale-x-0"
            }`}
          />

  
        </div>
      </div>
    </section>
  );
};

export default HowItWorks;
